import React, { useLayoutEffect, useRef, useSyncExternalStore } from "react";
import { Eye } from "lucide-react";
import { LAYERS, T } from "../app/tokens.js";
import { getRoleDisplayLabel } from "../contexts/SensitivityPolicyContext.jsx";
import { getSelfPresentation, subscribeFeedback, getFeedbackSnapshot } from "../services/devActAsFeedback.js";
import { transitionDevIdentity } from "../services/devIdentityTransition.js";

export function impersonationPresentation(user, role, status, self) {
  if (status?.enabled !== true) return null;
  const person = status.person;
  if (person?.id && typeof status.version === "string" && status.version &&
      user?.devActAs?.person?.id === person.id &&
      user.devActAs.version === status.version && !status.invalid) {
    return { mode: "person", label: person.displayName || person.email || "selected person",
      role: person.role, self: self?.name || "your signed-in self", selfRole: null };
  }
  if (!person && !status.invalid && !user?.devActAs && user?.role === "admin" &&
      ["editor", "viewer"].includes(role)) {
    return { mode: "role", label: getRoleDisplayLabel(role),
      self: self?.name || "your signed-in self", selfRole: self?.role };
  }
  return null;
}

// Fixed above every shell scroll owner; measured height also reaches body portals.
export function ImpersonationBanner({ currentUser, currentRole, status, onExitPreview }) {
  const ref = useRef(null);
  const { enabled, feedback } = useSyncExternalStore(subscribeFeedback, getFeedbackSnapshot);
  const model = enabled ? impersonationPresentation(currentUser, currentRole, status,
    getSelfPresentation(currentUser, status)) : null;
  const active = Boolean(model);
  useLayoutEffect(() => {
    const root = document.documentElement;
    const measure = () => root.style.setProperty("--impersonation-height", `${ref.current?.getBoundingClientRect().height || 0}px`);
    measure();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    if (ref.current) observer?.observe(ref.current);
    window.addEventListener("resize", measure);
    return () => { observer?.disconnect(); window.removeEventListener("resize", measure); root.style.removeProperty("--impersonation-height"); };
  }, [active]);
  if (!model) return null;
  const pending = ["request", "reload"].includes(feedback?.phase);
  const stop = () => {
    if (model.mode === "role") onExitPreview?.();
    else transitionDevIdentity(status).catch(() => { /* shared feedback shows the verbatim error */ });
  };
  return <aside ref={ref} data-testid="impersonation-banner" aria-label="DEV impersonation"
    style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: LAYERS.stickyChrome + 3,
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
      minHeight: 42, padding: "8px 22px", boxSizing: "border-box", fontSize: T.fsSm,
      background: T.impersonationBg, color: T.impersonationText, borderBottom: `1px solid ${T.impersonationBorder}` }}>
    <span role="status" style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
      <Eye size={16} aria-hidden="true" style={{ flexShrink: 0 }} />
      <span style={{ overflowWrap: "anywhere" }}>
        {model.mode === "person" ? "Acting as " : "Previewing as "}<strong>{model.label}</strong>
        {model.role && <> · {getRoleDisplayLabel(model.role)}</>} · you are {model.self}
        {model.selfRole && <> ({getRoleDisplayLabel(model.selfRole)})</>}
      </span>
    </span>
    <button type="button" data-testid={model.mode === "role" ? "exit-role-preview" : "banner-stop-acting"}
      onClick={stop} disabled={pending} style={{ flexShrink: 0, border: `1px solid ${T.impersonationText}`,
        borderRadius: 6, padding: "4px 10px", background: "transparent", color: "inherit",
        font: "inherit", fontWeight: T.fwSemibold, cursor: pending ? "wait" : "pointer" }}>
      {model.mode === "person" ? "Stop acting as" : "Exit preview"}
    </button>
  </aside>;
}
